// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPackage, FiRefreshCcw } from "react-icons/fi";
import NoctisStatusDot from "../../components/noctis/NoctisStatusDot";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { integrationApi } from "../../api/apiClient";

const NoctisObjectTypes = () => {
  const { t } = useTranslation();
  const [objectTypes, setObjectTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawDrawerOpen, setRawDrawerOpen] = useState(false);
  const [typeDrawerOpen, setTypeDrawerOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const loadObjectTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await integrationApi.get("/integration/object-types");
      setObjectTypes(res?.data || []);
    } catch (e) {
      const message = e?.response?.data?.message || e?.message || "Failed to load object types.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadObjectTypes();
  }, []);

  useEffect(() => {
    if (!rawDrawerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setRawDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [rawDrawerOpen]);

  useEffect(() => {
    if (!typeDrawerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setTypeDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [typeDrawerOpen]);

  const columns = useMemo(() => {
    if (!Array.isArray(objectTypes) || objectTypes.length === 0) return [];
    const keys = new Set();
    objectTypes.forEach((item) => {
      Object.keys(item || {}).forEach((k) => keys.add(k));
    });
    const preferred = ["name", "code", "description", "sourceSystem", "externalId", "id"];
    const orderedPreferred = preferred.filter((k) => keys.has(k));
    const remaining = Array.from(keys).filter((k) => !preferred.includes(k)).sort();
    return [...orderedPreferred, ...remaining];
  }, [objectTypes]);

  const renderCell = (col, value) => {
    if (value == null || value === "") return "—";
    if (col === "code") {
      return <span className="inline-flex items-center rounded-lg bg-indigo-50 px-2 py-1 font-mono text-xs font-semibold text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-100">{String(value)}</span>;
    }
    if (col === "description") {
      return <span className="block max-w-3xl leading-relaxed text-gray-800 dark:text-gray-100">{String(value)}</span>;
    }
    return String(value);
  };

  const openTypeDrawer = (type) => {
    setSelectedType(type || null);
    setTypeDrawerOpen(true);
  };

  const renderJsonBlock = (value) => {
    if (value == null) return <span className="text-gray-500 dark:text-gray-400">—</span>;
    if (typeof value !== "object") return <span className="break-words">{String(value)}</span>;
    return (
      <pre className="mt-2 overflow-auto rounded-2xl bg-gray-900 p-4 text-xs text-gray-100">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  };

  const formatDateValue = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString();
  };

  const renderValue = (value, { mono = false } = {}) => {
    if (value == null || value === "") return <span className="text-gray-500 dark:text-gray-400">—</span>;
    if (typeof value === "boolean") {
      return (
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
            value
              ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
              : "bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200"
          }`}
        >
          {value ? t("common.yes", { defaultValue: "Yes" }) : t("common.no", { defaultValue: "No" })}
        </span>
      );
    }
    const formattedDate = typeof value === "string" ? formatDateValue(value) : null;
    const display = formattedDate || String(value);
    return (
      <span className={`break-words ${mono ? "font-mono text-xs font-semibold" : ""}`}>
        {display}
      </span>
    );
  };

  const renderKeyValue = (label, value, options) => {
    return (
      <div className="rounded-xl border border-gray-100/80 bg-white/80 p-3 shadow-sm dark:border-gray-800/60 dark:bg-slate-950/40">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
        <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">{renderValue(value, options)}</div>
      </div>
    );
  };

  const retentionRule =
    selectedType?.retentionRule ??
    selectedType?.retentionrule ??
    selectedType?.retention_rule ??
    null;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FiPackage}
        eyebrow={t("noctis.menu", { defaultValue: "Noctis" })}
        title={t("noctis.title")}
        description={t("noctis.subtitle")}
        actions={
          <>
            <NoctisStatusDot />
            <button
              type="button"
              onClick={() => setRawDrawerOpen(true)}
              className="rounded-full border border-indigo-100/70 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
              disabled={loading || !Array.isArray(objectTypes) || objectTypes.length === 0}
            >
              {t("noctis.raw", { defaultValue: "Ruwe data" })}
            </button>
            <button
              type="button"
              onClick={loadObjectTypes}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loading}
            >
              <FiRefreshCcw />
              {t("noctis.refresh")}
            </button>
          </>
        }
      />
      <div className="space-y-6 text-gray-900 dark:text-gray-100">
        <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          {error && (
            <div className="mt-4 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
              {error}
            </div>
          )}
        </div>

        <LdsTableCard>
          <LdsTableScroll>
              <LdsTable>
                <LdsTableHead>
                  <LdsTableRow>
                    {columns.length > 0 ? (
                      columns.map((col) => (
                        <LdsTableHeadCell
                          key={col}
                          scope="col"
                        >
                          {t(`noctis.table.${col}`, { defaultValue: col })}
                        </LdsTableHeadCell>
                      ))
                    ) : (
                      <LdsTableHeadCell>
                        {t("noctis.table.name")}
                      </LdsTableHeadCell>
                    )}
                  </LdsTableRow>
                </LdsTableHead>
                <LdsTableBody>
                  {loading && (
                    <LdsTableRow>
                      <LdsTableCell className="text-gray-500 dark:text-gray-300" colSpan={columns.length || 1}>
                        {t("common.loading", { defaultValue: "Loading..." })}
                      </LdsTableCell>
                    </LdsTableRow>
                  )}

                  {!loading && objectTypes.length === 0 && (
                    <LdsTableRow>
                      <LdsTableCell className="text-gray-500 dark:text-gray-300" colSpan={columns.length || 1}>
                        {t("noctis.table.empty")}
                      </LdsTableCell>
                    </LdsTableRow>
                  )}

                  {!loading &&
                    objectTypes.length > 0 &&
                    objectTypes.map((item, idx) => (
                      <LdsTableRow
                        key={idx}
                        className="cursor-pointer"
                        onClick={() => openTypeDrawer(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") openTypeDrawer(item);
                        }}
                      >
                        {columns.map((col) => (
                          <LdsTableCell key={col} className="text-gray-800 dark:text-gray-100 align-top">
                            {renderCell(col, item?.[col])}
                          </LdsTableCell>
                        ))}
                      </LdsTableRow>
                    ))}
                </LdsTableBody>
              </LdsTable>
          </LdsTableScroll>
        </LdsTableCard>
      </div>

      {/* Drawer (raw object types) */}
      {rawDrawerOpen && (
        <div className="fixed inset-0 z-[9999]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
            onClick={() => setRawDrawerOpen(false)}
            aria-label={t("common.close", { defaultValue: "Close" })}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto border-l border-indigo-100/60 bg-white/95 p-6 shadow-2xl dark:border-indigo-500/30 dark:bg-slate-950/95">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Noctis
                </div>
                <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t("noctis.raw", { defaultValue: "Ruwe data" })}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {t("noctis.object_types", { defaultValue: "Objecttypes" })}
                </p>
              </div>
              <button
                type="button"
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                onClick={() => setRawDrawerOpen(false)}
              >
                {t("common.close", { defaultValue: "Close" })}
              </button>
            </div>

            <pre className="mt-4 overflow-auto rounded-2xl bg-gray-900 p-4 text-xs text-gray-100">
              {JSON.stringify(objectTypes, null, 2)}
            </pre>
          </aside>
        </div>
      )}

      {/* Drawer (selected object type details) */}
      {typeDrawerOpen && (
        <div className="fixed inset-0 z-[9999]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
            onClick={() => setTypeDrawerOpen(false)}
            aria-label={t("common.close", { defaultValue: "Close" })}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto border-l border-indigo-100/60 bg-white/95 p-6 shadow-2xl dark:border-indigo-500/30 dark:bg-slate-950/95">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Noctis
                </div>
                <h3 className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {selectedType?.name || selectedType?.code || t("noctis.object_type", { defaultValue: "Objecttype" })}
                </h3>
                {selectedType?.description && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{String(selectedType.description)}</p>
                )}
              </div>
              <button
                type="button"
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                onClick={() => setTypeDrawerOpen(false)}
              >
                {t("common.close", { defaultValue: "Close" })}
              </button>
            </div>

            <div className="mt-6 space-y-5">
              <div className="rounded-2xl border border-indigo-100/70 bg-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-slate-950">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t("noctis.details", { defaultValue: "Details" })}
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t("noctis.table.name", { defaultValue: "name" })}
                    </div>
                    <div className="mt-1 break-words text-gray-900 dark:text-gray-100">{selectedType?.name ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t("noctis.table.code", { defaultValue: "code" })}
                    </div>
                    <div className="mt-1 break-words text-gray-900 dark:text-gray-100">{selectedType?.code ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t("noctis.table.sourceSystem", { defaultValue: "sourceSystem" })}
                    </div>
                    <div className="mt-1 break-words text-gray-900 dark:text-gray-100">
                      {selectedType?.sourceSystem ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t("noctis.table.externalId", { defaultValue: "externalId" })}
                    </div>
                    <div className="mt-1 break-words text-gray-900 dark:text-gray-100">
                      {selectedType?.externalId ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t("noctis.table.id", { defaultValue: "id" })}
                    </div>
                    <div className="mt-1 break-words text-gray-900 dark:text-gray-100">{selectedType?.id ?? "—"}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-indigo-100/70 bg-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-slate-950">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t("noctis.retention_rule", { defaultValue: "Retention rule" })}
                </div>
                {retentionRule && typeof retentionRule === "object" ? (
                  <>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      {renderKeyValue("Code", retentionRule?.code, { mono: true })}
                      {renderKeyValue("Category", retentionRule?.category, { mono: true })}
                      {renderKeyValue("Retention period", retentionRule?.retentionPeriod)}
                      {renderKeyValue("Active", retentionRule?.active)}
                      {renderKeyValue("Retention years", retentionRule?.retentionYears)}
                      {renderKeyValue("Retention months", retentionRule?.retentionMonths)}
                      {renderKeyValue("Legal basis", retentionRule?.legalBasis)}
                      {renderKeyValue("Retention text", retentionRule?.retentionText)}
                      {renderKeyValue("Created", retentionRule?.createdAt)}
                      {renderKeyValue("Updated", retentionRule?.updatedAt)}
                      {renderKeyValue("Id", retentionRule?.id, { mono: true })}
                    </div>
                  </>
                ) : (
                  renderJsonBlock(retentionRule)
                )}
              </div>

              <details className="rounded-2xl border border-indigo-100/70 bg-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-slate-950">
                <summary className="cursor-pointer text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                  {t("noctis.raw", { defaultValue: "Ruwe data" })}
                </summary>
                <div className="mt-3 space-y-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t("noctis.object_type", { defaultValue: "Objecttype" })}
                    </div>
                    {renderJsonBlock(selectedType)}
                  </div>
                  {retentionRule && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t("noctis.retention_rule", { defaultValue: "Retention rule" })}
                      </div>
                      {renderJsonBlock(retentionRule)}
                    </div>
                  )}
                </div>
              </details>
            </div>
          </aside>
        </div>
      )}
    </LdsOverviewPage>
  );
};

export default NoctisObjectTypes;
